import React from 'react';
import { createPortal } from 'react-dom';
import DateTimePopover from '../../../../../ui/DateTimePopover';
import { hasWhenAvailable, REMINDER_TRIGGER, removeReminder } from '../../../../../../utils/missionReminders';
import { chipLabel, chipTitle } from '../utils/reminderLabels';

const BellIcon = ({ size = 12 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const stop = (e) => e.stopPropagation();

/**
 * Chips, add button, and the portaled reminder menus shared by card and sidebar.
 */
export default function RemindMeCluster({
  className,
  reminders,
  canEdit,
  showAdd,
  addLabel,
  addPlaceholder,
  hasStartReminder,
  startLabel,
  persist,
  picker,
}) {
  const {
    pickerOpen,
    triggerRef,
    pickerRef,
    pickerPos,
    session,
    openPicker,
    openEditor,
    confirmPicker,
    closePicker,
    chooseWhenAvailable,
    chooseTimeToStart,
    setNote,
    setDraftDate,
    setPickerMode,
    setRecurDraft,
  } = picker;

  const chips = reminders.map((reminder) => {
    const canEditTime = canEdit && reminder.trigger === REMINDER_TRIGGER.AT_TIME && reminder.at;
    return (
      <span
        key={reminder.id}
        className={`remind-me__chip${canEditTime ? ' remind-me__chip--editable' : ''}`}
        title={chipTitle(reminder)}
      >
        {canEditTime ? (
          <button
            type="button"
            className="remind-me__chip-text remind-me__chip-edit"
            title="Edit reminder"
            onClick={(e) => openEditor(reminder, e)}
            onMouseDown={stop}
          >
            {chipLabel(reminder)}
          </button>
        ) : (
          <span className="remind-me__chip-text">{chipLabel(reminder)}</span>
        )}
        {canEdit && (
          <button
            type="button"
            className="remind-me__chip-remove"
            title="Remove reminder"
            onClick={(e) => {
              e.stopPropagation();
              persist(removeReminder(reminders, reminder.id));
            }}
            onMouseDown={stop}
          >
            ×
          </button>
        )}
      </span>
    );
  });

  const lockedMenu = pickerOpen && session.step === 'menu' && createPortal(
    <div
      ref={pickerRef}
      className="date-time-popover remind-me__picker"
      style={{ top: pickerPos.top, left: pickerPos.left }}
      onClick={stop}
      onMouseDown={stop}
    >
      <h2 className="date-time-popover__title">Set Reminder</h2>
      <div className="date-time-popover__choices">
        {!hasWhenAvailable(reminders) && (
          <button type="button" className="date-time-popover__choice" onClick={chooseWhenAvailable}>
            <span className="date-time-popover__choice-label">When available</span>
            <span className="date-time-popover__choice-hint">Ping when this mission unlocks</span>
          </button>
        )}
        {!hasStartReminder && (
          <button type="button" className="date-time-popover__choice" onClick={chooseTimeToStart}>
            <span className="date-time-popover__choice-label">When time to start</span>
            <span className="date-time-popover__choice-hint">
              {startLabel ? `Ping at ${startLabel}` : 'Uses the mission start time'}
            </span>
          </button>
        )}
        {session.startHint && (
          <p className="remind-me__start-hint">{session.startHint}</p>
        )}
      </div>
    </div>,
    document.body
  );

  const timePicker = pickerOpen && session.step === 'time' && createPortal(
    <DateTimePopover
      ref={pickerRef}
      className="remind-me__picker"
      style={{ top: pickerPos.top, left: pickerPos.left }}
      title="Set Reminder"
      chooseMode
      showRecurring
      initialMode={session.initialMode}
      onModeChange={setPickerMode}
      recurSeed={session.recurSeed}
      onRecurChange={setRecurDraft}
      selected={session.draftDate}
      onChange={(date) => setDraftDate(date || null)}
      onDismiss={closePicker}
      onClick={stop}
      onMouseDown={stop}
      showTimeSelect
      dateFormat="dd/MM/yyyy HH:mm"
      shouldCloseOnSelect={false}
    >
      <label className="remind-me__picker-note">
        <span className="remind-me__note-label">Note</span>
        <input
          type="text"
          className="remind-me__note-input"
          value={session.note}
          maxLength={160}
          placeholder="Shown in the notification"
          onChange={(e) => setNote(e.target.value)}
          onClick={stop}
          onMouseDown={stop}
        />
      </label>
      <div className="remind-me__picker-actions">
        <button
          type="button"
          className="remind-me__set"
          disabled={session.pickerMode === 'recurring' ? !session.recurDraft : !session.draftDate}
          onClick={confirmPicker}
        >
          {session.editingId ? 'Update Reminder' : 'Set Reminder'}
        </button>
      </div>
    </DateTimePopover>,
    document.body
  );

  return (
    <div className={className} onMouseDown={stop}>
      {chips}
      {showAdd && (
        <button
          ref={triggerRef}
          type="button"
          className={`remind-me__add${addPlaceholder ? ' remind-me__add--placeholder' : ''}`}
          onClick={openPicker}
          onMouseDown={stop}
        >
          <BellIcon size={12} />
          <span>{addLabel}</span>
        </button>
      )}
      {lockedMenu}
      {timePicker}
    </div>
  );
}
